import { useState, useCallback } from "react";
import { AutoTaggingResult, ClothingItem, ImageUpload } from "../types/unified-services";

export interface AutoTaggingState {
  image: ImageUpload;
  result: AutoTaggingResult | null;
  selectedClothing: ClothingItem | null;
  loadingResult: boolean;
}

export interface AutoTaggingActions {
  setImageFile: (file: File | null) => void;
  setImageBase64: (base64: string) => void;
  setResult: (result: AutoTaggingResult | null) => void;
  setSelectedClothing: (clothing: ClothingItem | null) => void;
  setLoadingResult: (loading: boolean) => void;
  resetAutoTagging: () => void;
}

export const useAutoTaggingState = (): AutoTaggingState & AutoTaggingActions => {
  const [image, setImage] = useState<ImageUpload>({
    file: null,
    base64: null,
    preview: null,
    isValid: false,
  });
  const [result, setResult] = useState<AutoTaggingResult | null>(null);
  const [selectedClothing, setSelectedClothing] = useState<ClothingItem | null>(null);
  const [loadingResult, setLoadingResult] = useState(false);

  const setImageFile = useCallback((file: File | null) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        const base64Data = base64.split(",")[1];
        setImage({
          file,
          base64: base64Data,
          preview: base64,
          isValid: true,
        });
      };
      reader.readAsDataURL(file);
    } else {
      setImage({
        file: null,
        base64: null,
        preview: null,
        isValid: false,
      });
    }
  }, []);

  const setImageBase64 = useCallback((base64: string) => {
    setImage((prev) => ({
      ...prev,
      base64: base64.split(",")[1],
      preview: base64.startsWith("data:") ? base64 : `data:image/jpeg;base64,${base64}`,
      isValid: !!base64,
    }));
  }, []);

  const resetAutoTagging = useCallback(() => {
    setImage({
      file: null,
      base64: null,
      preview: null,
      isValid: false,
    });
    setResult(null);
    setSelectedClothing(null);
    setLoadingResult(false);
  }, []);

  return {
    image,
    result,
    selectedClothing,
    loadingResult,

    setImageFile,
    setImageBase64,
    setResult,
    setSelectedClothing,
    setLoadingResult,
    resetAutoTagging,
  };
};
